import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { CheckCircle2, Star, ExternalLink, Flag, MessageSquarePlus, Loader2 } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/atoms/sheet';
import { Button } from '@/components/atoms/button';
import { Textarea } from '@/components/atoms/textarea';
import { Skeleton } from '@/components/atoms/skeleton';
import { Badge } from '@/components/atoms/badge';
import { Label } from '@/components/atoms/label';
import { useReviewDetail } from '@/hooks/reviews/useReviewDetail';
import {
  useAddInternalNote,
  useFlagReview,
  useLinkExternalReviewAppointment,
  useSetTestimonialApproval,
  useSetResponseApproval,
} from '@/hooks/reviews/useReviewActions';
import { cn } from '@/lib/utils';
import { formatDateTimeInTimeZone } from '@/lib/date-values';
import { useCompanyTimezone } from '@/hooks/settings/useCompanyTimezone';
import {
  parseUnifiedReviewId,
  type ExternalReviewDetail,
  type UnifiedReviewItem,
  type ReviewResponseDetail,
  type ReviewStoryAttachment,
  type TestimonialDetail,
} from '@/types/review';
import { AiReplyComposer } from './AiReplyComposer';
import { StoryPhotoCarousel } from './StoryPhotoCarousel';
import { SentimentTagPills } from './SentimentTagPills';
import { AttributionConfidenceNote } from './AttributionConfidenceNote';
import { isUnratedReview, UNRATED_LABEL } from './unrated-review';

interface ReviewDetailDrawerProps {
  item: UnifiedReviewItem | null;
  /**
   * Media for the visit behind this review. Only the stories view knows it —
   * the unified review row carries no photos — so the list view opens the same
   * drawer without a strip rather than fetching one.
   */
  photos?: ReviewStoryAttachment[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Caller permissions — drives reply/flag/note availability. */
  canReply: boolean;
  canEdit: boolean;
  /**
   * `reviews:approve` — publishing a testimonial to the anonymous public page.
   * Deliberately not implied by `canEdit`: annotating a review and publishing a
   * customer's words are different powers (see the RBAC catalog entry).
   */
  canApprove?: boolean;
  /**
   * Fired after a visit link or unlink succeeds. The mutation invalidates the
   * review lists, but a cursor-paginated view only refetches the page it is
   * currently sitting on — this lets the caller reload the pages it holds.
   */
  onAttributionChange?: () => void;
}

function StarRow({ rating }: { rating: number }) {
  // Same rule as the list row and the export: a zero here is an unrated import,
  // not a nought-star review. See `unrated-review.ts`.
  if (isUnratedReview({ rating })) {
    return (
      <span className="text-xs text-muted-foreground" aria-label="No star rating">
        {UNRATED_LABEL}
      </span>
    );
  }
  return (
    <div className="flex items-center gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={cn(
            'h-4 w-4',
            i < rating ? 'fill-yellow-400 text-yellow-400' : 'fill-muted text-muted',
          )}
        />
      ))}
    </div>
  );
}

/**
 * Right-hand drawer for a unified review row (spec §12.4). Loads the rich
 * detail when the source supports it (currently `response` only on the admin
 * controller), falling back to the row's snippet otherwise.
 */
export function ReviewDetailDrawer({
  item,
  photos,
  open,
  onOpenChange,
  canReply,
  canEdit,
  canApprove = false,
  onAttributionChange,
}: ReviewDetailDrawerProps) {
  const companyTz = useCompanyTimezone();
  const parsed = useMemo(() => (item ? parseUnifiedReviewId(item.id) : null), [item]);

  // Pull rich detail for every unified source.
  const detail = useReviewDetail(parsed?.kind ?? 'response', parsed?.id ?? null, {
    enabled: !!item && !!parsed,
  });
  const responseDetail: ReviewResponseDetail | undefined =
    parsed?.kind === 'response'
      ? (detail.data?.data as ReviewResponseDetail | undefined)
      : undefined;
  const externalDetail: ExternalReviewDetail | undefined =
    parsed?.kind === 'external'
      ? (detail.data?.data as ExternalReviewDetail | undefined)
      : undefined;
  const testimonialDetail: TestimonialDetail | undefined =
    parsed?.kind === 'testimonial'
      ? (detail.data?.data as TestimonialDetail | undefined)
      : undefined;

  /**
   * Prefer the freshly-fetched row over the list snapshot: the list is
   * cursor-paginated and a page the user is sitting on can be several
   * approvals stale. `item.isApprovedForDisplay` is the fallback for the
   * moment before the detail query resolves.
   */
  const isApproved =
    testimonialDetail?.isApprovedForDisplay ??
    responseDetail?.isApprovedForDisplay ??
    item?.isApprovedForDisplay ??
    false;

  const addNote = useAddInternalNote();
  const setApproval = useSetTestimonialApproval();
  const setResponseApproval = useSetResponseApproval();

  /**
   * Both publishable sources, and the one asymmetry between them.
   *
   * A testimonial was captured through a form whose whole purpose was public
   * display, so approval is the only gate. A review response was captured under
   * the landing page's private promise unless the customer lifted it, so
   * `publishConsent` gates it as well — and no staff action can supply that.
   * Approving a response nobody consented to publishes nothing, which is why
   * the control says so rather than appearing to work.
   */
  const publishable =
    parsed?.kind === 'testimonial'
      ? {
          kind: 'testimonial' as const,
          id: parsed.id,
          consented: true,
          noun: 'Testimonials',
        }
      : parsed?.kind === 'response'
        ? {
            kind: 'response' as const,
            id: parsed.id,
            // Falls back to the list row rather than to `false`, so an
            // in-flight detail query cannot make a consented response look
            // unconsented. See #2427.
            consented: (responseDetail?.publishConsent ?? item?.publishConsent) === true,
            noun: 'Reviews',
          }
        : null;
  const approvalPending = setApproval.isPending || setResponseApproval.isPending;
  const flag = useFlagReview();
  const linkAppointment = useLinkExternalReviewAppointment();
  const [noteDraft, setNoteDraft] = useState('');
  const [flagComment, setFlagComment] = useState('');
  const [appointmentDraft, setAppointmentDraft] = useState('');
  const [editingAttribution, setEditingAttribution] = useState(false);
  const [showFlagForm, setShowFlagForm] = useState(false);

  useEffect(() => {
    setAppointmentDraft('');
    setEditingAttribution(false);
  }, [item?.id, open]);

  const handleAttributionSuccess = useCallback(() => {
    setAppointmentDraft('');
    setEditingAttribution(false);
    onAttributionChange?.();
  }, [onAttributionChange]);

  const initialReplyDraft = useMemo(() => {
    // ExternalReview's replyDraft JSON isn't surfaced through the unified
    // list directly — composer fetches/regenerates as needed.
    return externalDetail && 'replyDraft' in externalDetail
      ? (externalDetail.replyDraft?.text ?? undefined)
      : undefined;
  }, [externalDetail]);

  if (!item) {
    // Render nothing when there's no selected item — Radix Dialog requires a
    // Title for a11y, and there's no content to surface anyway.
    return null;
  }

  const author = item.author?.trim() || 'Anonymous';
  const linkedAppointmentId =
    parsed?.kind === 'external'
      ? externalDetail
        ? (externalDetail.linkedAppointmentId ?? null)
        : (item.linkedAppointmentId ?? null)
      : (responseDetail?.appointmentId ?? item.linkedAppointmentId ?? null);
  const linkedTechId =
    parsed?.kind === 'external'
      ? externalDetail
        ? (externalDetail.linkedTechId ?? null)
        : (item.linkedTechId ?? null)
      : (item.linkedTechId ?? null);
  const linkedTechName =
    parsed?.kind === 'external'
      ? externalDetail
        ? (externalDetail.linkedTechName ?? null)
        : (item.linkedTechName ?? null)
      : (item.linkedTechName ?? null);
  // Resolved the same way as the link above, and for the same reason. After Link visit
  // or Remove link the detail query is invalidated immediately, while the row in the
  // cursor-paginated list can stay stale — so reading the note from `item` could print
  // "2 visits matched this name" directly above a freshly linked visit. Raised in
  // review on #2536.
  //
  // Detail-first rather than `detail ?? item`: once the detail has loaded it is
  // authoritative, including when its value is null. Falling back to the list snapshot
  // on a null would put the stale message back for the unlink case, which is the case
  // that motivated this.
  const attributionConfidence =
    parsed?.kind === 'external' && externalDetail
      ? (externalDetail.attributionConfidence ?? undefined)
      : item.attributionConfidence;
  const ambiguousCandidateCount =
    parsed?.kind === 'external' && externalDetail
      ? (externalDetail.ambiguousCandidateCount ?? undefined)
      : item.ambiguousCandidateCount;
  // The job was resolved server-side for the ROW's appointment. If the detail
  // payload names a different one, neither the id nor the number describes
  // what we are about to render — labelling it "Job 12" would be a wrong
  // answer rather than a missing one.
  const jobDescribesThisAppointment =
    !!linkedAppointmentId &&
    (parsed?.kind === 'external'
      ? !!externalDetail
      : linkedAppointmentId === item.linkedAppointmentId);
  const linkedJobId = jobDescribesThisAppointment
    ? (externalDetail?.linkedJobId ?? item.linkedJobId ?? null)
    : null;
  // Job number when we have one. A missing/deleted relation is rendered as
  // unavailable; UUID fragments are not business references.
  const linkedJobNumber = jobDescribesThisAppointment
    ? (externalDetail?.linkedJobNumber ?? item.linkedJobNumber)
    : undefined;
  const linkedJobLabel = linkedJobNumber
    ? `Job ${linkedJobNumber}`
    : linkedAppointmentId
      ? 'Appointment unavailable'
      : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="flex w-full sm:!max-w-xl flex-col gap-0 overflow-y-auto p-0"
        side="right"
      >
        <SheetHeader className="border-b px-6 py-4">
          {/*
            `pr-8` clears the sheet's own close button, which is positioned
            `absolute top-4 right-4 size-8` — i.e. it owns the first 48px from
            the right edge. `justify-between` puts the stars at the header's
            24px padding, underneath it. Fixed here rather than in the sheet
            atom: moving the button or padding the atom would shift every other
            Sheet in the app, and this is the only header that pushes content
            into that corner.
          */}
          <div className="flex items-center justify-between gap-3 pr-8">
            <SheetTitle className="text-base">{author}</SheetTitle>
            <StarRow rating={item.rating} />
          </div>
          <SheetDescription className="text-[11px]">
            {formatDateTimeInTimeZone(item.postedAt, companyTz, {
              dateStyle: 'medium',
              timeStyle: 'short',
            })}
            {item.platform ? ` · ${item.platform}` : ''}
            {item.replyStatus ? ` · ${item.replyStatus}` : ''}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6 px-6 py-5">
          {photos?.length ? (
            <section className="space-y-2">
              <Label className="text-xs font-semibold">Photos</Label>
              <div className="overflow-hidden rounded-md border">
                <StoryPhotoCarousel photos={photos} />
              </div>
            </section>
          ) : null}
          {/* Review text */}
          <section className="space-y-2">
            <Label className="text-xs font-semibold">Review</Label>
            <p className="whitespace-pre-wrap text-sm leading-relaxed">
              {item.text || 'No free-text feedback.'}
            </p>
            <SentimentTagPills tags={item.sentimentTags} />
          </section>

          {publishable ? (
            <section className="space-y-2 rounded-md border bg-muted/30 p-3">
              <Label className="text-xs font-semibold">Public display</Label>
              {!publishable.consented ? (
                <p className="text-xs text-muted-foreground">
                  This customer did not agree to have their words published, so it cannot appear on
                  your public page. Only the customer can change that, on the review link.
                </p>
              ) : isApproved ? (
                <p className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700">
                  <CheckCircle2 className="size-4" /> Published on your public job-stories page
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {publishable.kind === 'response'
                    ? 'The customer agreed to share this. It stays off the public page until you clear it.'
                    : 'Captured but not published. Testimonials stay off the public page until someone clears them.'}
                </p>
              )}
              {canApprove && publishable.consented ? (
                <Button
                  type="button"
                  variant={isApproved ? 'ghost' : 'default'}
                  size="sm"
                  className="h-7 text-xs"
                  disabled={approvalPending}
                  onClick={() =>
                    publishable.kind === 'testimonial'
                      ? setApproval.mutate({
                          testimonialId: publishable.id,
                          approved: !isApproved,
                        })
                      : setResponseApproval.mutate({
                          reviewResponseId: publishable.id,
                          approved: !isApproved,
                        })
                  }
                >
                  {approvalPending ? <Loader2 className="mr-1.5 size-3.5 animate-spin" /> : null}
                  {isApproved ? 'Remove from public page' : 'Approve for public page'}
                </Button>
              ) : canApprove ? null : (
                <p className="text-xs text-muted-foreground">
                  You do not have permission to publish {publishable.noun.toLowerCase()}.
                </p>
              )}
            </section>
          ) : null}

          {parsed?.kind === 'external' && canEdit ? (
            <section className="space-y-2 rounded-md border bg-muted/30 p-3">
              <Label className="text-xs font-semibold">Visit attribution</Label>
              {/*
                Directly under the heading so it qualifies the green check below it
                rather than trailing after the actions. A link matched on name and date
                is a guess, and the form further down already tells the user that names
                and dates are not proof — without this the automatic path contradicts
                that warning.
              */}
              <AttributionConfidenceNote
                confidence={attributionConfidence}
                ambiguousCandidateCount={ambiguousCandidateCount}
              />
              {linkedAppointmentId && !editingAttribution ? (
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="inline-flex items-center gap-1.5 font-medium text-emerald-700">
                    <CheckCircle2 className="size-4" /> Linked completed visit
                  </span>
                  {linkedJobId ? (
                    <Link
                      to="/dashboard/jobs/$id/appointments/$appointmentId"
                      params={{ id: linkedJobId, appointmentId: linkedAppointmentId }}
                      className="text-primary hover:underline"
                    >
                      {linkedJobLabel}
                    </Link>
                  ) : (
                    <span className="text-muted-foreground">{linkedJobLabel}</span>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-0 text-xs"
                    onClick={() => setEditingAttribution(true)}
                  >
                    Change visit
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-0 text-xs text-muted-foreground"
                    disabled={linkAppointment.isPending}
                    onClick={() =>
                      linkAppointment.mutate(
                        { externalReviewId: parsed.id, appointmentId: null },
                        { onSuccess: handleAttributionSuccess },
                      )
                    }
                  >
                    Remove link
                  </Button>
                </div>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground">
                    External platforms do not provide a Notifi visit ID. Link only when you have
                    verified the completed visit; matching names and dates alone are not proof.
                  </p>
                  <div className="flex gap-2">
                    <Textarea
                      value={appointmentDraft}
                      onChange={(event) => setAppointmentDraft(event.target.value)}
                      placeholder="Paste completed visit UUID"
                      className="min-h-9 flex-1 text-xs"
                      rows={1}
                    />
                    <Button
                      type="button"
                      size="sm"
                      disabled={!appointmentDraft.trim() || linkAppointment.isPending}
                      onClick={() => {
                        if (!parsed.id) return;
                        linkAppointment.mutate(
                          {
                            externalReviewId: parsed.id,
                            appointmentId: appointmentDraft.trim(),
                          },
                          { onSuccess: handleAttributionSuccess },
                        );
                      }}
                    >
                      Link visit
                    </Button>
                  </div>
                </>
              )}
            </section>
          ) : null}

          {/* Reply composer */}
          <section className="space-y-2">
            <AiReplyComposer
              item={item}
              rawId={parsed?.id ?? null}
              canReply={canReply}
              initialDraft={initialReplyDraft}
            />
          </section>

          {/* Linked context */}
          <section className="space-y-2 rounded-md border bg-muted/30 p-3">
            <Label className="text-xs font-semibold">Linked context</Label>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              {linkedAppointmentId && linkedJobId ? (
                // The appointment route is nested under its job, so the link
                // exists only when both ids do. Labelled with the job number:
                // "Appointment 736d5763" named a UUID nobody can look up.
                <Link
                  to="/dashboard/jobs/$id/appointments/$appointmentId"
                  params={{ id: linkedJobId, appointmentId: linkedAppointmentId }}
                  className="inline-flex items-center gap-1 text-primary hover:underline"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  {linkedJobLabel}
                </Link>
              ) : linkedAppointmentId ? (
                // No job to hang the route off — the appointment was deleted,
                // or never belonged to one. Plain text rather than a link that
                // cannot resolve.
                <span className="inline-flex items-center gap-1 text-muted-foreground">
                  {linkedJobLabel}
                </span>
              ) : (
                <span className="text-muted-foreground">No linked appointment.</span>
              )}
              {linkedTechId ? (
                <Badge variant="outline" className="h-5 text-[10px]">
                  {linkedTechName || 'Technician unavailable'}
                </Badge>
              ) : null}
              {responseDetail?.internalTaskId ? (
                <Badge variant="outline" className="h-5 text-[10px]">
                  CS task created
                </Badge>
              ) : null}
            </div>
          </section>

          {/* Internal notes — only for ReviewResponse rows */}
          {parsed?.kind === 'response' ? (
            <section className="space-y-2">
              <Label className="text-xs font-semibold">Internal notes</Label>
              {detail.isLoading ? (
                <Skeleton className="h-16 w-full" />
              ) : (
                <div className="space-y-2">
                  {(responseDetail?.sentimentAnalysis?.notes ?? []).map((n, i) => (
                    <div key={i} className="rounded-md border bg-card p-2 text-xs">
                      <div className="text-[10px] text-muted-foreground">
                        {formatDateTimeInTimeZone(n.at, companyTz, {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        })}
                      </div>
                      <p className="mt-1 whitespace-pre-wrap">{n.note}</p>
                    </div>
                  ))}
                  {responseDetail?.sentimentAnalysis?.escalation ? (
                    <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs">
                      <div className="flex items-center gap-1 text-[10px] font-medium text-amber-700">
                        <Flag className="h-3 w-3" />
                        Flagged for escalation ·{' '}
                        {formatDateTimeInTimeZone(
                          responseDetail.sentimentAnalysis.escalation.flaggedAt,
                          companyTz,
                          { dateStyle: 'medium', timeStyle: 'short' },
                        )}
                      </div>
                      <p className="mt-1 whitespace-pre-wrap text-amber-900">
                        {responseDetail.sentimentAnalysis.escalation.comment}
                      </p>
                    </div>
                  ) : null}

                  {canEdit && parsed?.id ? (
                    <div className="space-y-1.5">
                      <Textarea
                        placeholder="Add an internal note…"
                        value={noteDraft}
                        onChange={(e) => setNoteDraft(e.target.value)}
                        className="text-sm min-h-[64px]"
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={!noteDraft.trim() || addNote.isPending}
                        onClick={() => {
                          if (!parsed?.id) return;
                          addNote.mutate(
                            { reviewResponseId: parsed.id, dto: { note: noteDraft.trim() } },
                            { onSuccess: () => setNoteDraft('') },
                          );
                        }}
                      >
                        {addNote.isPending ? (
                          <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <MessageSquarePlus className="mr-1.5 h-3.5 w-3.5" />
                        )}
                        Add note
                      </Button>
                    </div>
                  ) : null}
                </div>
              )}
            </section>
          ) : null}

          {/* Flag for escalation — only available for ReviewResponse rows. */}
          {parsed?.kind === 'response' && canEdit ? (
            <section className="space-y-2">
              <Label className="text-xs font-semibold">Escalation</Label>
              {!showFlagForm ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFlagForm(true)}
                >
                  <Flag className="mr-1.5 h-3.5 w-3.5" />
                  Flag for escalation
                </Button>
              ) : (
                <div className="space-y-1.5">
                  <Textarea
                    placeholder="Why are you escalating this review?"
                    value={flagComment}
                    onChange={(e) => setFlagComment(e.target.value)}
                    className="text-sm min-h-[64px]"
                  />
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="destructive"
                      disabled={!flagComment.trim() || flag.isPending}
                      onClick={() => {
                        if (!parsed?.id) return;
                        flag.mutate(
                          { reviewResponseId: parsed.id, dto: { comment: flagComment.trim() } },
                          {
                            onSuccess: () => {
                              setFlagComment('');
                              setShowFlagForm(false);
                            },
                          },
                        );
                      }}
                    >
                      {flag.isPending ? (
                        <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                      ) : null}
                      Flag
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowFlagForm(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </section>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}
